<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS } from '@/services/mockData';
import { playersApi } from '@/services/api';
import type { PlayerProfile, PlayerPosition, BattingHand, ThrowingHand, PlayerStatus } from '@/types/models';
import AppBadge from '@/components/ui/AppBadge.vue';
import AppModal from '@/components/ui/AppModal.vue';
import PlayerDetailModal from '@/components/roster/PlayerDetailModal.vue';
import SeatAllocatorModal from '@/components/SeatAllocatorModal.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NInput, NButton, NSelect, NCard, NTag } from 'naive-ui';
import {
  Users,
  Plus,
  Search,
  ArrowLeft,
  Mail,
  Phone,
  Eye,
  UserPlus,
  Sparkles,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  Clock,
  Link,
  Copy,
  RefreshCw
} from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { activeTenant, managedTeams, currentUser } = useTenant();
const { notifySuccess, notifyInfo, notifyError } = useToastNotification();

const isCoachOrAdmin = computed(() =>
  currentUser.value.role === 'ADMIN' ||
  currentUser.value.role === 'COACH' ||
  currentUser.value.role === 'HEAD_COACH' ||
  currentUser.value.role === 'ORG_ADMIN' ||
  currentUser.value.role === 'TEAM_ADMIN'
);

const isTeamAdmin = computed(() =>
  currentUser.value.role === 'ADMIN' ||
  currentUser.value.role === 'TEAM_ADMIN' ||
  currentUser.value.role === 'ORG_ADMIN'
);

const teamId = computed(() => (route.params.id as string) || managedTeams.value[0]?.id || INITIAL_TEAMS[0].id);
const currentTeam = computed(() => {
  return managedTeams.value.find(t => t.id === teamId.value) || INITIAL_TEAMS[0];
});

const players = ref<PlayerProfile[]>([]);
const searchQuery = ref('');
const positionFilter = ref<string>('ALL');
const isAddPlayerOpen = ref(false);
const isDetailOpen = ref(false);
const isSeatAllocatorOpen = ref(false);
const selectedPlayer = ref<PlayerProfile | null>(null);
const isLoading = ref(false);

const loadRoster = async () => {
  isLoading.value = true;
  try {
    const list = await playersApi.getPlayers(teamId.value, activeTenant.value.id);
    players.value = list || [];
  } catch (err) {
    console.warn('[RosterView] Load error:', err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadRoster();
});

watch([() => teamId.value, () => activeTenant.value.id], () => {
  loadRoster();
});

const positionOptions = computed(() => [
  { label: t('roster.allPositions'), value: 'ALL' },
  { label: 'Pitchers (P)', value: 'P' },
  { label: 'Catchers (C)', value: 'C' },
  { label: 'Shortstops (SS)', value: 'SS' },
  { label: 'Third Base (3B)', value: '3B' },
  { label: 'Outfield (CF/LF/RF)', value: 'CF' },
]);

const filteredPlayers = computed(() => {
  return players.value
    .filter(p => {
      if (positionFilter.value !== 'ALL' && p.position !== positionFilter.value) return false;
      const q = searchQuery.value.toLowerCase();
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      return fullName.includes(q) || String(p.jerseyNumber).includes(q) || p.position.toLowerCase().includes(q);
    });
});

const openPlayerDetail = (player: PlayerProfile) => {
  selectedPlayer.value = player;
  isDetailOpen.value = true;
};

const handlePlayerUpdated = async (updated: PlayerProfile) => {
  const idx = players.value.findIndex(p => p.id === updated.id);
  if (idx !== -1) {
    players.value[idx] = updated;
  }
  await playersApi.updatePlayer(updated.id, updated);
  notifySuccess(t('roster.playerAdded'), `#${updated.jerseyNumber} ${updated.firstName} ${updated.lastName}`);
};

const toggleDuesStatus = async (player: PlayerProfile) => {
  const nextStatus = player.individualDuesStatus === 'PAID' ? 'PENDING' : 'PAID';
  player.individualDuesStatus = nextStatus;
  await playersApi.updatePlayer(player.id, { individualDuesStatus: nextStatus });
  notifySuccess('Dues Status Updated', `#${player.jerseyNumber} ${player.lastName} set to ${nextStatus}`);
};

const copyStripeInvoiceLink = (player: PlayerProfile) => {
  const link = `https://checkout.stripe.com/pay/cs_live_${player.id}_season2025`;
  navigator.clipboard.writeText(link);
  notifyInfo('Stripe Link Copied', `Copied payment link for ${player.firstName} ${player.lastName}`);
};

// Form state
const playerForm = ref({
  firstName: '',
  lastName: '',
  jerseyNumber: 1,
  position: 'P' as PlayerPosition,
  secondaryPosition: '' as PlayerPosition | '',
  bats: 'R' as BattingHand,
  throws: 'R' as ThrowingHand,
  status: 'ACTIVE' as PlayerStatus,
  email: '',
  phone: '',
  graduationYear: 2026,
});

const isSubmitting = ref(false);

const handleAddPlayer = async () => {
  if (!playerForm.value.firstName || !playerForm.value.lastName) return;

  isSubmitting.value = true;
  try {
    const created = await playersApi.createPlayer(currentTeam.value.id, {
      tenantId: activeTenant.value.id,
      firstName: playerForm.value.firstName,
      lastName: playerForm.value.lastName,
      jerseyNumber: Number(playerForm.value.jerseyNumber),
      position: playerForm.value.position,
      secondaryPosition: playerForm.value.secondaryPosition || undefined,
      bats: playerForm.value.bats,
      throws: playerForm.value.throws,
      status: playerForm.value.status,
      email: playerForm.value.email || undefined,
      phone: playerForm.value.phone || undefined,
      graduationYear: Number(playerForm.value.graduationYear),
      individualDuesStatus: 'PENDING',
    });

    players.value.push(created);
    isAddPlayerOpen.value = false;
    notifySuccess(t('roster.playerAdded'), t('roster.playerAddedMsg', { num: created.jerseyNumber, first: created.firstName, last: created.lastName }));

    // Reset form
    playerForm.value = {
      firstName: '',
      lastName: '',
      jerseyNumber: 10,
      position: 'SS',
      secondaryPosition: '',
      bats: 'R',
      throws: 'R',
      status: 'ACTIVE',
      email: '',
      phone: '',
      graduationYear: 2026,
    };
  } catch (err: any) {
    notifyError('Add Player Error', err.message);
  } finally {
    isSubmitting.value = false;
  }
};

const getStatusBadgeVariant = (status: PlayerStatus) => {
  switch (status) {
    case 'ACTIVE': return 'success';
    case 'INJURED': return 'danger';
    case 'RESERVE': return 'warning';
    default: return 'slate';
  }
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header with Back to Teams -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div class="flex items-center gap-3">
        <button
          type="button"
          @click="router.push('/dashboard/teams')"
          class="p-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 transition"
          :title="t('roster.backToTeams')"
        >
          <ArrowLeft class="w-4 h-4" />
        </button>

        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-extrabold text-slate-100 tracking-tight">
              {{ currentTeam.name }}
            </h1>
            <AppBadge variant="primary">{{ currentTeam.division }}</AppBadge>
          </div>
          <p class="text-xs text-slate-400 mt-0.5">
            {{ t('teams.headCoach') }} <strong class="text-slate-200">{{ currentTeam.headCoach }}</strong> &bull; {{ filteredPlayers.length }} {{ t('roster.activeAthletes') }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2.5">
        <NButton
          secondary
          @click="loadRoster"
          :loading="isLoading"
          class="font-semibold text-xs"
          :title="t('teams.syncWithApi')"
        >
          <template #icon>
            <RefreshCw class="w-4 h-4 text-sky-400" />
          </template>
          {{ t('common.sync') }}
        </NButton>

        <NButton
          secondary
          @click="router.push('/dashboard/lineup')"
          class="font-semibold text-xs"
        >
          {{ t('roster.lineupCard') }}
        </NButton>

        <NButton
          v-if="isCoachOrAdmin"
          secondary
          type="info"
          @click="isSeatAllocatorOpen = true"
          class="font-semibold text-xs"
        >
          <template #icon>
            <ShieldCheck class="w-4 h-4 text-sky-400" />
          </template>
          {{ t('roster.seatAllocations') }}
        </NButton>

        <NButton
          v-if="isCoachOrAdmin"
          type="primary"
          @click="isAddPlayerOpen = true"
          class="font-bold text-xs"
        >
          <template #icon>
            <UserPlus class="w-4 h-4" />
          </template>
          {{ t('roster.addPlayer') }}
        </NButton>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
      <div class="relative max-w-sm flex-1">
        <NInput
          v-model:value="searchQuery"
          :placeholder="t('roster.searchPlaceholder')"
          clearable
          size="medium"
        >
          <template #prefix>
            <Search class="w-4 h-4 text-slate-400" />
          </template>
        </NInput>
      </div>

      <div class="w-48">
        <NSelect
          v-model:value="positionFilter"
          :options="positionOptions"
          size="medium"
        />
      </div>
    </div>

    <!-- Roster DataTable -->
    <div class="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-xs">
          <thead>
            <tr class="border-b border-slate-800/80 bg-slate-950/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              <th class="py-3.5 px-4 text-center w-14">#</th>
              <th class="py-3.5 px-4">{{ t('roster.playerName') }}</th>
              <th class="py-3.5 px-4">{{ t('roster.primaryPos') }}</th>
              <th class="py-3.5 px-4">{{ t('roster.batsThrows') }}</th>
              <th class="py-3.5 px-4">{{ t('roster.class') }}</th>
              <th class="py-3.5 px-4">{{ t('roster.status') }}</th>
              <th v-if="isTeamAdmin" class="py-3.5 px-4 text-center">Stripe Dues</th>
              <th class="py-3.5 px-4 text-right">{{ t('roster.actions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800/60">
            <tr
              v-for="player in filteredPlayers"
              :key="player.id"
              class="hover:bg-slate-800/40 transition group cursor-pointer"
              @click="openPlayerDetail(player)"
            >
              <!-- Jersey Number -->
              <td class="py-3.5 px-4 text-center font-mono font-extrabold text-sm text-sky-400">
                #{{ player.jerseyNumber }}
              </td>

              <!-- Name -->
              <td class="py-3.5 px-4">
                <div class="font-bold text-slate-200 group-hover:text-sky-300 transition">
                  {{ player.firstName }} {{ player.lastName }}
                </div>
                <div v-if="player.secondaryPosition" class="text-[10px] text-slate-500">
                  Sec: {{ player.secondaryPosition }}
                </div>
              </td>

              <!-- Position -->
              <td class="py-3.5 px-4">
                <AppBadge variant="position">{{ player.position }}</AppBadge>
              </td>

              <!-- Bats / Throws Split -->
              <td class="py-3.5 px-4 font-mono text-slate-300">
                {{ player.bats }} / {{ player.throws }}
              </td>

              <!-- Graduation Year -->
              <td class="py-3.5 px-4 text-slate-400">
                {{ player.graduationYear || '2026' }}
              </td>

              <!-- Availability Status -->
              <td class="py-3.5 px-4">
                <AppBadge :variant="getStatusBadgeVariant(player.status)">
                  {{ player.status }}
                </AppBadge>
              </td>

              <!-- Team Admin: Stripe Dues Status Toggle -->
              <td v-if="isTeamAdmin" class="py-3.5 px-4 text-center" @click.stop>
                <div class="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    @click="toggleDuesStatus(player)"
                    :class="[
                      'px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase transition flex items-center gap-1',
                      player.individualDuesStatus === 'PAID'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                    ]"
                  >
                    <CheckCircle2 v-if="player.individualDuesStatus === 'PAID'" class="w-3 h-3" />
                    <Clock v-else class="w-3 h-3" />
                    <span>{{ player.individualDuesStatus || 'PENDING' }}</span>
                  </button>

                  <button
                    type="button"
                    @click="copyStripeInvoiceLink(player)"
                    class="p-1 hover:text-sky-400 text-slate-500 transition"
                    title="Copy Stripe Checkout Paylink"
                  >
                    <Copy class="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>

              <!-- Actions -->
              <td class="py-3.5 px-4 text-right" @click.stop>
                <NButton
                  size="tiny"
                  secondary
                  @click="openPlayerDetail(player)"
                  :title="t('roster.viewProfile')"
                >
                  <template #icon>
                    <Eye class="w-3.5 h-3.5" />
                  </template>
                </NButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredPlayers.length === 0" class="text-center py-12 text-slate-500 text-xs">
        {{ t('roster.noRecords') }}
      </div>
    </div>

    <!-- Modal: Add Player -->
    <AppModal
      v-model="isAddPlayerOpen"
      :title="t('roster.addModalTitle')"
      :subtitle="t('roster.addModalSubtitle', { team: currentTeam.name })"
    >
      <form @submit.prevent="handleAddPlayer" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.firstName') }}</label>
            <NInput
              v-model:value="playerForm.firstName"
              placeholder="e.g. Bryce"
              required
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.lastName') }}</label>
            <NInput
              v-model:value="playerForm.lastName"
              placeholder="e.g. Harper"
              required
            />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.jersey') }}</label>
            <input
              v-model.number="playerForm.jerseyNumber"
              type="number"
              min="0"
              max="99"
              required
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.position') }}</label>
            <select
              v-model="playerForm.position"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            >
              <option value="P">P - Pitcher</option>
              <option value="C">C - Catcher</option>
              <option value="1B">1B - First Base</option>
              <option value="2B">2B - Second Base</option>
              <option value="3B">3B - Third Base</option>
              <option value="SS">SS - Shortstop</option>
              <option value="LF">LF - Left Field</option>
              <option value="CF">CF - Center Field</option>
              <option value="RF">RF - Right Field</option>
              <option value="DH">DH - Designated Hitter</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.batsThrows') }}</label>
            <div class="grid grid-cols-2 gap-1">
              <select v-model="playerForm.bats" class="bg-slate-950 border border-slate-800 rounded-xl px-1.5 py-2 text-xs text-slate-100">
                <option value="R">R</option>
                <option value="L">L</option>
                <option value="S">S</option>
              </select>
              <select v-model="playerForm.throws" class="bg-slate-950 border border-slate-800 rounded-xl px-1.5 py-2 text-xs text-slate-100">
                <option value="R">R</option>
                <option value="L">L</option>
              </select>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.emailOptional') }}</label>
            <NInput
              v-model:value="playerForm.email"
              placeholder="athlete@example.com"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('roster.gradYear') }}</label>
            <input
              v-model.number="playerForm.graduationYear"
              type="number"
              min="2024"
              max="2032"
              class="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
            />
          </div>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-800">
          <NButton
            quaternary
            @click="isAddPlayerOpen = false"
          >
            {{ t('teams.cancel') }}
          </NButton>
          <NButton
            type="primary"
            attr-type="submit"
            :loading="isSubmitting"
            class="font-bold text-xs"
          >
            {{ t('roster.addAthlete') }}
          </NButton>
        </div>
      </form>
    </AppModal>

    <!-- Player Detail Scouting Modal -->
    <PlayerDetailModal
      v-model="isDetailOpen"
      :player="selectedPlayer"
      @update:player="handlePlayerUpdated"
    />

    <!-- Seat Allocator Modal -->
    <SeatAllocatorModal
      v-model:show="isSeatAllocatorOpen"
      service-id="VIDEO_ANALYTICS"
      service-name="Video Analytics & AI Replay"
      :team-id="currentTeam.id"
      @updated="loadRoster"
    />
  </div>
</template>
